import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaCreditCard, FaEdit, FaList, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import AdminPageHeader from './AdminPageHeader';
import Navigation from './Navigation';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Memberships() {
  const [memberships, setMemberships] = useState([]);
  const [filteredMemberships, setFilteredMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // For outlet filtering display
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(0); // 0 means "All Outlets"

  const navigate = useNavigate();

  // Fetch membership list from backend
  const fetchMemberships = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}membership/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ limit: 100, offset: 0, outlet: selectedOutlet })
      });
      const data = await response.json();
      console.log('Memberships fetched:', data.results);  // Debug log
      if (data.error === 0 && Array.isArray(data.results)) {
        setMemberships(data.results);
      } else {
        setMemberships([]);
        Swal.fire('Error', 'Failed to fetch membership data', 'error');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setMemberships([]);
      Swal.fire('Error', 'Failed to fetch membership data', 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedOutlet]);

  // Fetch outlets to allow filtering by outlet name
  const fetchOutlets = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      Swal.fire('Error', 'Session expired. Please log in again.', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}outletlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ limit: 100, offset: 0 })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Outlets fetched:', data);  // Improved logging

      if (data.error === "0" && Array.isArray(data.results)) {
        const formattedOutlets = data.results.map(outlet => ({
          ID: outlet.id,  // Keep as number
          OutletName: outlet.outlet_name
        }));
        console.log('Formatted Outlets:', formattedOutlets);  // Debug log
        setOutlets(formattedOutlets);
      } else {
        console.error('Failed to fetch outlets:', data.message || 'Unknown error');
        Swal.fire('Error', data.message || 'Failed to fetch outlets', 'error');
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
      Swal.fire('Error', 'Failed to fetch outlets', 'error');
    }
  }, []);

  // Ensure outlets are fetched first, then memberships
  useEffect(() => {
    const fetchData = async () => {
      await fetchOutlets();       // Fetch outlets first
      await fetchMemberships();   // Then fetch memberships
    };
    fetchData();
  }, [fetchOutlets, fetchMemberships]);

  // Apply search and outlet filtering on memberships
  useEffect(() => {
    let filtered = memberships;
    if (search.trim()) {
      filtered = filtered.filter(membership =>
        membership.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (selectedOutlet !== 0) {
      filtered = filtered.filter(membership => membership.outlet === selectedOutlet);
    }
    setFilteredMemberships(filtered);
  }, [memberships, search, selectedOutlet]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleOutletChange = (e) => {
    setSelectedOutlet(parseInt(e.target.value, 10));
  };

  const handleDelete = useCallback(async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      const token = localStorage.getItem('sessionToken');
      try {
        const response = await fetch(`${API_BASE_URL}membership/delete`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ id })
        });
        const data = await response.json();
        if (data.error === 0) {
          Swal.fire('Deleted!', 'Membership has been deleted.', 'success');
          fetchMemberships();
        } else {
          Swal.fire('Error!', 'Failed to delete membership.', 'error');
        }
      } catch (err) {
        console.error('Delete error:', err);
        Swal.fire('Error!', 'Failed to delete membership.', 'error');
      }
    }
  }, [fetchMemberships]);

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
    {
      name: 'Membership',
      sortable: true,
      grow: 2,
      cell: row => {
        const outlet = outlets.find(o => o.ID === row.outlet);
        return (
          <div className="table-identity">
            <span className="table-identity__avatar">
              {row.name?.slice(0, 2).toUpperCase() || 'MB'}
            </span>
            <span className="table-identity__meta">
              <strong>{row.name}</strong>
              <small>{outlet ? outlet.OutletName : 'N/A'}</small>
            </span>
          </div>
        );
      },
    },
    {
      name: 'Pricing',
      sortable: true,
      cell: row => (
        <span className="table-identity__meta">
          <strong>Rp {row.price.toLocaleString()}</strong>
          <small>Discount {row.discount}%</small>
        </span>
      ),
    },
    { name: 'Expired', selector: row => `${row.expired} days`, sortable: true },
    {
      name: 'Type',
      sortable: true,
      cell: row => (
        <span className={`status-pill ${row.type === 'Daily' ? 'is-warning' : ''}`}>{row.type}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="table-action-group">
          <Button variant="outline-warning" size="sm" onClick={() => navigate(`/edit-membership/${row.id}`)}>
            <FaEdit />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.id)}>
            <FaTrash />
          </Button>
        </div>
      ),
      width: '120px',
      right: true,
    },
  ];

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaCreditCard}
          eyebrow="Plan setup"
          title="Plan Management"
          description="Kelola paket atau plan per outlet, harga, durasi aktif, dan potongan harga."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Plan Management' },
          ]}
          actions={(
            <Button variant="primary" onClick={() => navigate('/add-membership')}>
              <FaPlus /> Add New Plan
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              <FaList /> {filteredMemberships.length} plans
            </span>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaList /> {filteredMemberships.length} plans
            </span>
            <div className="page-actions">
              <Form.Select
                value={selectedOutlet}
                onChange={handleOutletChange}
                className="filter-control"
              >
                <option value={0}>All Outlets</option>
                {outlets.map(outlet => (
                  <option key={outlet.ID} value={outlet.ID}>
                    {outlet.OutletName}
                  </option>
                ))}
              </Form.Select>
              <div className="control-with-icon search-control">
                <span className="control-with-icon__icon">
                  <FaSearch />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search membership name"
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>
          <AdminDataTable
            columns={columns}
            data={filteredMemberships}
            pagination
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            striped
            emptyTitle="No membership data found"
            emptyDescription="Tambahkan paket membership untuk mulai mengelola penjualan per outlet."
          />
        </div>
      </Container>
    </>
  );
}

export default Memberships;
